import { Component, type ErrorInfo, type ReactNode } from "react";
import * as Sentry from "@sentry/react";

type Props = { children: ReactNode };
type State = { hasError: boolean };

// M9: minimal inline i18n dict used BEFORE I18nProvider mounts (where I18nContext
// is unavailable). Picks the language from navigator.language → falls back to IT.
const FALLBACK_I18N = {
  it: { title: "Qualcosa è andato storto", body: "Si è verificato un errore imprevisto. Prova a ricaricare la pagina.", reload: "Ricarica" },
  de: { title: "Ein Fehler ist aufgetreten", body: "Ein unerwarteter Fehler ist aufgetreten. Bitte lade die Seite neu.", reload: "Neu laden" },
  fr: { title: "Une erreur s'est produite", body: "Une erreur inattendue est survenue. Veuillez recharger la page.", reload: "Recharger" },
  en: { title: "Something went wrong", body: "An unexpected error occurred. Please reload the page.", reload: "Reload" },
} as const;

function pickLang(): keyof typeof FALLBACK_I18N {
  if (typeof navigator === "undefined") return "it";
  const code = (navigator.language || "it").slice(0, 2).toLowerCase();
  return (code in FALLBACK_I18N ? code : "it") as keyof typeof FALLBACK_I18N;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary]", error, info);
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
  }

  render() {
    if (this.state.hasError) {
      const dict = FALLBACK_I18N[pickLang()];
      return (
        <div className="min-h-screen flex items-center justify-center bg-background px-6 text-center">
          <div className="max-w-md">
            <h1 className="text-2xl font-bold text-foreground mb-2">{dict.title}</h1>
            <p className="text-sm text-muted-foreground mb-6">{dict.body}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-primary text-primary-foreground rounded-full px-6 py-3 text-sm font-semibold hover:shadow-lg hover:shadow-primary/25 transition-all"
            >
              {dict.reload}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
